import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateGroupPermissionResponse,
  CreatePermissionResponse,
  GroupPermissionBody,
  GroupPermissionResponse,
  HistoryGroupPermissionResponse,
  PermissionBody,
  PermissionGroupResponse,
  PermissionHistoryResponse,
  PermissionOptionsResponse,
  SelectOption,
  UserPermissionBody,
  UserPermissionByIdResponse,
  UserPermissionGrid,
  Response,
} from "../../types";
import { translate } from "../../intl";
import { userSelector } from "../../redux/application/selectors";
import {
  userPermissionSelectorAll,
  thunks as userThunks,
} from "../../redux/user/slice";
import { orderAlphabetically } from "../../utils";
import {
  buildResponse,
  buildResponseWithMapper,
  callWorkflowDelete,
  callWorkflowGET,
  callWorkflowPOST,
  callWorkflowPUT,
  handleFeedback,
} from "../generic";
import { useAuthentication } from "../../hooks/useAuthentication";
import { USER_PERMISSION_ENDPOINTS } from "./endpoints";
import type { AppDispatch } from "../../app";

export const getUserPermissions = (
  setObject: (obj: Response<UserPermissionGrid[]>) => void,
): void => {
  const response = callWorkflowGET<UserPermissionGrid[]>(
    USER_PERMISSION_ENDPOINTS.USER_PERMISSION,
  );

  buildResponseWithMapper<UserPermissionGrid[], UserPermissionGrid[]>(
    response,
    setObject,
    (response) => orderAlphabetically(response, "name"),
  );
};

export const getUserPermissionById = (
  userId: number,
  setObject: (obj: Response<UserPermissionByIdResponse>) => void,
): void => {
  const response = callWorkflowGET<UserPermissionByIdResponse>(
    USER_PERMISSION_ENDPOINTS.USER_PERMISSION_BY_ID(userId),
  );

  buildResponse<UserPermissionByIdResponse>(response, setObject);
};

export interface GroupOptions extends SelectOption {
  permissions: number[];
}

export const getGroupOptions = (
  setObject: (obj: Response<GroupOptions[]>) => void,
): void => {
  const response = callWorkflowGET<GroupPermissionResponse[]>(
    USER_PERMISSION_ENDPOINTS.GROUP,
  );

  buildResponseWithMapper<GroupPermissionResponse[], GroupOptions[]>(
    response,
    setObject,
    (groups) =>
      groups?.map((group) => {
        return {
          id: group.id,
          label: group.name,
          permissions: group.permissions.map((item) => item.id),
        };
      }),
  );
};

export const getFullGroupOptions = (
  setObject: (obj: Response<GroupPermissionResponse[]>) => void,
): void => {
  const response = callWorkflowGET<GroupPermissionResponse[]>("group");
  buildResponse<GroupPermissionResponse[]>(response, setObject);
};

export const getPermissionOptions = (
  setObject: (obj: Response<PermissionOptionsResponse[]>) => void,
): void => {
  const response = callWorkflowGET<PermissionOptionsResponse[]>(
    USER_PERMISSION_ENDPOINTS.PERMISSION,
  );
  buildResponse<PermissionOptionsResponse[]>(response, setObject);
};

export const getPermissionByGroupId = (
  groupId: number,
  setObject: (obj: Response<PermissionGroupResponse>) => void,
): void => {
  const response = callWorkflowGET<PermissionGroupResponse>(
    USER_PERMISSION_ENDPOINTS.GROUP_BY_ID(groupId),
  );
  buildResponse<PermissionGroupResponse>(response, setObject);
};

export const getHistoryByGroupId = (
  groupId: number,
  setObject: (obj: Response<HistoryGroupPermissionResponse>) => void,
): void => {
  const response = callWorkflowGET<HistoryGroupPermissionResponse>(
    USER_PERMISSION_ENDPOINTS.GROUP_HISTORY(groupId),
  );
  buildResponse<HistoryGroupPermissionResponse>(response, setObject);
};

export const putGroupPermission = (
  id: number,
  body: GroupPermissionBody,
  setObject: (obj: Response<CreateGroupPermissionResponse>) => void,
) => {
  const response = callWorkflowPUT<
    CreateGroupPermissionResponse,
    GroupPermissionBody
  >(USER_PERMISSION_ENDPOINTS.GROUP_BY_ID(id), body);
  buildResponse<CreateGroupPermissionResponse>(response, setObject);
};

export const postGroupPermission = (
  body: GroupPermissionBody,
  setObject: (obj: Response<CreateGroupPermissionResponse>) => void,
) => {
  const response = callWorkflowPOST<
    CreateGroupPermissionResponse,
    GroupPermissionBody
  >(USER_PERMISSION_ENDPOINTS.GROUP, body);
  buildResponse<CreateGroupPermissionResponse>(response, setObject);
};

export const deleteGroupPermission = (
  id: number,
  action?: (obj: Response<any>) => void,
) => {
  const response = callWorkflowDelete(
    USER_PERMISSION_ENDPOINTS.GROUP_BY_ID(id),
  );
  handleFeedback(response, action, () =>
    translate("permissionPage.delete.successfullyDeleted"),
  );
};

export const postUserPermission = (
  body: UserPermissionBody,
  setObject: (obj: Response<CreatePermissionResponse>) => void,
  action: () => void,
): void => {
  const response = callWorkflowPOST<
    CreatePermissionResponse,
    UserPermissionBody
  >(USER_PERMISSION_ENDPOINTS.USER_PERMISSION, {
    ...body,
    email: body.email.trim(),
  });

  buildResponse<CreatePermissionResponse>(response, setObject, action);
};

export const putUserPermission = (
  userId: number,
  body: PermissionBody[],
  setObject: (obj: Response<CreatePermissionResponse>) => void,
  action: (obj: Response<CreatePermissionResponse>) => void,
): void => {
  const response = callWorkflowPUT<CreatePermissionResponse, PermissionBody[]>(
    USER_PERMISSION_ENDPOINTS.USER_PERMISSION_BY_ID(userId),
    body,
  );
  // @ts-ignore - TODO - WRONG TYPE, NEED FIX
  buildResponse<CreatePermissionResponse>(response, setObject, action);
};

export const deleteUserPermission = (
  userId: number,
  action: (obj: Response<any>) => void,
): void => {
  const axiosResponse = callWorkflowDelete(
    USER_PERMISSION_ENDPOINTS.USER_PERMISSION_BY_ID(userId),
  );
  handleFeedback(axiosResponse, action, () => translate("feedback.deleted"));
};

export const deleteUserGroup = (
  userId: number,
  userGroupId: number,
  action?: (obj: Response<any>) => void,
): void => {
  const axiosResponse = callWorkflowDelete(
    USER_PERMISSION_ENDPOINTS.USER_GROUP_PERMISSION(userId, userGroupId),
  );
  handleFeedback(axiosResponse, action, () => translate("feedback.deleted"));
};

export const getPermissionHistory = (
  userId: number,
  setObject: (obj: Response<PermissionHistoryResponse>) => void,
): void => {
  const response = callWorkflowGET<PermissionHistoryResponse>(
    USER_PERMISSION_ENDPOINTS.USER_PERMISSION_HISTORY(userId),
  );

  buildResponse<PermissionHistoryResponse>(response, setObject);
};

export const useUserPermission = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useAuthentication();
  const user = useSelector(userSelector);
  const userPermissions = useSelector(userPermissionSelectorAll);

  useEffect(() => {
    if (
      isAuthenticated &&
      user?.user_id &&
      !userPermissions.data?.user_groups
    ) {
      dispatch(userThunks.fetchUserPermissionById(user.user_id));
    }
  }, [
    dispatch,
    isAuthenticated,
    user?.user_id,
    userPermissions.data?.user_groups,
  ]);

  return { userPermissions, isLoading: userPermissions.loading !== false };
};
